import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';

import { ErrorState, LoadingState } from '@/components/admin';
import { useClient } from '@/features/clients/hooks';
import { CaseReportEditor } from '@/features/sessions/components/CaseReportEditor';
import { useCaseReports, useSession } from '@/features/sessions/hooks';
import { requirePermission } from '@/lib/guards';
import { surfacePaths } from '@/lib/paths';
import { useAuthStore } from '@/stores/authStore';

export const Route = createFileRoute(
  '/_staff/admin/workspace/sessions/$sessionId/case-reports/$reportId',
)({
  beforeLoad: () => {
    requirePermission('case_report:read');
  },
  component: CaseReportScreen,
  // Editor page owns the full canvas — no shell padding around the card.
  staticData: { fullBleed: true },
});

export function CaseReportScreen() {
  const { sessionId, reportId } = useParams({
    from: '/_staff/admin/workspace/sessions/$sessionId/case-reports/$reportId',
  });
  const navigate = useNavigate();
  const sessionQ = useSession(sessionId);
  const reportsQuery = useCaseReports(sessionId);
  const clientQ = useClient(sessionQ.data?.clientId ?? undefined);
  const userFirstName = useAuthStore((s) => s.user?.firstName ?? 'you');
  const paths = surfacePaths('workspace');

  if (sessionQ.isLoading || reportsQuery.isLoading) return <LoadingState variant="form" />;
  if (sessionQ.error || reportsQuery.error) {
    const err = reportsQuery.error ?? sessionQ.error;
    return (
      <ErrorState
        description={err instanceof Error ? err.message : undefined}
        onRetry={() => {
          void reportsQuery.refetch();
          void sessionQ.refetch();
        }}
      />
    );
  }
  if (sessionQ.data === undefined) return <p className="p-6">Session not found.</p>;

  const report = reportsQuery.data?.find((r) => r.id === reportId);
  if (!report) {
    return <ErrorState title="Case report not found" />;
  }

  const session = sessionQ.data;
  const client = clientQ.data;
  const clientName =
    client !== undefined
      ? `${client.firstName} ${client.lastName}`
      : (session.clientName ?? 'Client');

  const returnToSession = () => {
    void navigate({ to: paths.sessions.detail(sessionId) });
  };

  // Mirror the clinical-surface route — full-bleed editor, no
  // JournalPageHeader / StatusTimeline wrapper. The new ClinicalEditorShell
  // carries its own chrome + side panel.
  return (
    <CaseReportEditor
      sessionId={sessionId}
      practitionerId={session.practitionerId}
      clientId={session.clientId}
      clientName={clientName}
      scheduledStart={session.scheduledStart}
      authorName={userFirstName}
      report={report}
      onSubmitted={returnToSession}
    />
  );
}
